/*
  Waifu Overlord checkout flow (Black November 2026)
  One page, one step at a time: email → plan → extras → loading → Shopify checkout.
  1. Steps        shows one [data-ovc-step] at a time, browser back/forward works (#email, #plan, #foil ...)
  2. Email        checks the address, tells Klaviyo, saves it for the checkout pre-fill
  3. Plan         shirt size (required), popups, pay later tooltip
  4. Extras       "add" / "no thanks" on each extra
  5. Checkout     empties the cart (optional), adds the free goodies, then plan + extras, opens Shopify checkout
  6. Media        lazy videos, swipe galleries, drag to scroll, one-line titles
  Choices are kept in sessionStorage, so a refresh doesn't lose them.
*/
(function () {
  'use strict';

  var d = document;
  var w = window;
  var KEY = 'ovc-state-v1';
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var designMode = !!(w.Shopify && w.Shopify.designMode);
  var reduceMotion = w.matchMedia ? w.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var canHover = w.matchMedia ? w.matchMedia('(hover: hover) and (pointer: fine)') : { matches: false };
  var hasIO = 'IntersectionObserver' in w;
  var root = (w.Shopify && w.Shopify.routes && w.Shopify.routes.root) || '/';

  /* ---------- State ---------- */
  var state = (function () {
    try { return JSON.parse(w.sessionStorage.getItem(KEY)) || {}; } catch (e) { return {}; }
  })();
  if (!state.extras || typeof state.extras !== 'object') state.extras = {};
  function save() {
    try { w.sessionStorage.setItem(KEY, JSON.stringify(state)); } catch (e) {}
  }
  function readJSON(sel, scope) {
    var el = (scope || d).querySelector(sel);
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  }
  function each(sel, fn, scope) { Array.prototype.forEach.call((scope || d).querySelectorAll(sel), fn); }

  /* ==========================================================================
     1. Steps
     ========================================================================== */
  var steps = [];
  var current = -1;

  function collectSteps() {
    steps = Array.prototype.map.call(d.querySelectorAll('[data-ovc-step]'), function (el) {
      return { key: el.getAttribute('data-ovc-step'), el: el, progress: parseInt(el.getAttribute('data-ovc-progress'), 10) || 1 };
    });
  }
  function indexOf(key) {
    for (var i = 0; i < steps.length; i++) if (steps[i].key === key) return i;
    return -1;
  }
  // No skipping ahead: email before the plan, a shirt size before the extras
  function allowed(i) {
    var s = steps[i];
    if (!s) return false;
    if (designMode) return true;
    if (s.key !== 'email' && indexOf('email') > -1 && !state.email) return false;
    if (s.progress >= 3 && indexOf('plan') > -1 && !state.size) return false;
    return true;
  }
  function firstAllowedBefore(i) {
    for (var j = Math.min(i, steps.length - 1); j >= 0; j--) if (allowed(j) && steps[j].key !== 'checkout') return j;
    return 0;
  }

  function setProgress(n) {
    each('[data-ovc-seg]', function (seg) {
      seg.classList.toggle('is-on', parseInt(seg.getAttribute('data-ovc-seg'), 10) <= n);
    });
    each('[data-ovc-stepnum]', function (li) {
      var k = parseInt(li.getAttribute('data-ovc-stepnum'), 10);
      li.classList.toggle('is-done', k < n);
      li.classList.toggle('is-current', k === n);
      if (k === n) li.setAttribute('aria-current', 'step'); else li.removeAttribute('aria-current');
    });
  }

  function show(i, keepScroll) {
    var s = steps[i];
    if (!s) return;
    steps.forEach(function (t, j) { t.el.hidden = j !== i; });
    current = i;
    setProgress(s.progress);
    if (!keepScroll) w.scrollTo(0, 0);
    // Move focus to the step for screen readers, without scrolling
    var heading = s.el.querySelector('h1, h2');
    if (heading && !designMode) {
      heading.setAttribute('tabindex', '-1');
      try { heading.focus({ preventScroll: true }); } catch (e) {}
    }
    fitText();
    prefetchNext(i);
    track(s.key);
  }

  function go(i, mode) {
    if (i < 0 || i >= steps.length) return;
    if (!allowed(i)) i = firstAllowedBefore(i);
    var key = steps[i].key;
    if (key === 'checkout') { startCheckout(); return; }
    var depth = (w.history.state && w.history.state.ovcDepth) || 0;
    try {
      if (mode === 'replace') w.history.replaceState({ ovc: key, ovcDepth: depth }, '', '#' + key);
      else w.history.pushState({ ovc: key, ovcDepth: depth + 1 }, '', '#' + key);
    } catch (e) {}
    show(i);
  }
  function next() { go(current + 1); }
  function back() {
    if (w.history.state && w.history.state.ovcDepth > 0) w.history.back();
    else go(Math.max(0, current - 1), 'replace');
  }

  w.addEventListener('popstate', function (e) {
    var key = (e.state && e.state.ovc) || w.location.hash.slice(1);
    var i = indexOf(key);
    if (i < 0) i = 0;
    if (steps[i] && steps[i].key === 'checkout') i = Math.max(0, i - 1);
    hideHandoff();
    if (!allowed(i)) i = firstAllowedBefore(i);
    show(i);
  });

  // Coming back from Shopify checkout with the browser back button
  w.addEventListener('pageshow', function (e) {
    if (e.persisted) {
      hideHandoff();
      if (current >= 0 && steps[current] && steps[current].key === 'checkout') show(Math.max(0, current - 1));
    }
  });

  function track(key) {
    try {
      if (w.Shopify && w.Shopify.analytics && typeof w.Shopify.analytics.publish === 'function') {
        w.Shopify.analytics.publish('overlord_checkout_step', { step: key });
      }
      if (w.dataLayer && typeof w.dataLayer.push === 'function') w.dataLayer.push({ event: 'overlord_checkout_step', step: key });
    } catch (e) {}
  }

  d.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-ovc-back]');
    if (btn && !btn.closest('[data-ovc-step="checkout"]')) { e.preventDefault(); back(); }
  });

  /* ==========================================================================
     2. Email
     ========================================================================== */
  function initEmail() {
    var form = d.querySelector('[data-ovc-email-form]');
    if (!form || form._ovc) return;
    form._ovc = true;
    var input = form.querySelector('[data-ovc-email-input]');
    var field = form.querySelector('[data-ovc-field]');
    var error = field && field.querySelector('.ovc-field__error');
    var cfg = readJSON('[data-ovc-email-config]') || {};
    if (state.email && input && !input.value) input.value = state.email;

    function mark(valid, showError) {
      if (!field) return;
      field.classList.toggle('is-valid', valid);
      field.classList.toggle('is-error', !valid && showError);
      if (error) error.hidden = valid || !showError;
      if (input) input.setAttribute('aria-invalid', !valid && showError ? 'true' : 'false');
    }
    if (input) {
      input.addEventListener('input', function () {
        var v = input.value.trim();
        if (field.classList.contains('is-error')) mark(EMAIL_RE.test(v), true);
        else if (EMAIL_RE.test(v)) mark(true, false);
        else mark(false, false);
      });
      if (input.value) mark(EMAIL_RE.test(input.value.trim()), false);
    }
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = input ? input.value.trim() : '';
      if (!EMAIL_RE.test(email)) {
        mark(false, true);
        if (input) input.focus();
        return;
      }
      mark(true, false);
      state.email = email;
      save();
      klaviyo(cfg, email);
      next();
    });
  }

  function klaviyo(cfg, email) {
    // Link this browser to the email (works whenever Klaviyo is installed on the store)
    try {
      if (w.klaviyo && typeof w.klaviyo.identify === 'function') w.klaviyo.identify({ email: email });
      else { w._learnq = w._learnq || []; w._learnq.push(['identify', { $email: email }]); }
    } catch (e) {}
    // Optional: add to a list (needs the public key + list ID in the theme editor)
    if (!cfg.klaviyoKey || !cfg.klaviyoList || !w.fetch) return;
    try {
      w.fetch('https://a.klaviyo.com/client/subscriptions?company_id=' + encodeURIComponent(cfg.klaviyoKey), {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: cfg.source || 'Overlord checkout',
              profile: { data: { type: 'profile', attributes: { email: email } } }
            },
            relationships: { list: { data: { type: 'list', id: cfg.klaviyoList } } }
          }
        })
      }).catch(function () {});
    } catch (e) {}
  }

  /* ==========================================================================
     3. Plan
     ========================================================================== */
  var planCfg = null;

  function initPlan() {
    var sec = d.querySelector('[data-ovc-step="plan"]');
    if (!sec || sec._ovc) return;
    sec._ovc = true;
    planCfg = readJSON('[data-ovc-plan-config]', sec) || { variants: [] };
    var btn = sec.querySelector('[data-ovc-plan-continue]');
    var bar = sec.querySelector('[data-ovc-planbar]');
    var hint = sec.querySelector('[data-ovc-plan-hint]');
    var radios = sec.querySelectorAll('[data-ovc-size]');

    var toggle = sec.querySelector('[data-ovc-shirt-toggle]');
    var shirt = sec.querySelector('[data-ovc-shirt]');
    var picked = sec.querySelector('[data-ovc-shirt-picked]');
    var phone = w.matchMedia ? w.matchMedia('(max-width: 1023.98px)') : { matches: true };
    function setOpen(open) {
      if (!toggle || !shirt) return;
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      shirt.classList.toggle('is-collapsed', !open);
    }
    function refresh() {
      var ok = !!state.size;
      if (picked) { picked.textContent = state.size || ''; picked.hidden = !ok; }
      if (btn) btn.disabled = !ok;
      if (bar) bar.classList.toggle('is-ready', ok);
      if (hint) hint.setAttribute('aria-hidden', ok ? 'true' : 'false');
      updatePlanPrice(sec);
    }
    Array.prototype.forEach.call(radios, function (r) {
      if (state.size && r.value === state.size && !r.disabled) r.checked = true;
      r.addEventListener('change', function () {
        if (!r.checked) return;
        state.size = r.value;
        state.sizeMatch = r.getAttribute('data-match') || r.value;
        save();
        refresh();
        // Phones: fold the size card away once a size is picked (tap the header to change it)
        if (phone.matches) setTimeout(function () { setOpen(false); }, 250);
      });
    });
    // A saved size that is now sold out is dropped
    if (state.size && !sec.querySelector('[data-ovc-size]:checked')) { delete state.size; delete state.sizeMatch; save(); }
    refresh();

    if (btn) btn.addEventListener('click', function () { if (state.size) next(); });

    // Phones: the shirt card folds away. The whole header row toggles it.
    if (toggle && shirt) {
      if (state.size && phone.matches) setOpen(false);
      var head = shirt.querySelector('.ovc-shirt__head');
      (head || toggle).addEventListener('click', function (e) {
        if (!phone.matches || e.target.closest('[data-ovc-open]')) return;
        setOpen(toggle.getAttribute('aria-expanded') !== 'true');
      });
    }

    // Pay later card: the card opens the popup, the (i) shows the tooltip
    var bnpl = sec.querySelector('[data-ovc-bnpl]');
    if (bnpl) {
      var tipBtn = bnpl.querySelector('[data-ovc-tip-toggle]');
      bnpl.addEventListener('click', function (e) {
        if (e.target.closest('[data-ovc-tip-toggle]') || e.target.closest('[data-ovc-tip]')) return;
        if (e.target.closest('[data-ovc-open]')) return; // the link handles itself
        openDialog(sec, 'bnpl');
      });
      if (tipBtn) {
        tipBtn.addEventListener('mouseenter', function () { if (canHover.matches) bnpl.classList.add('is-tip'); });
        tipBtn.addEventListener('mouseleave', function () { if (canHover.matches) bnpl.classList.remove('is-tip'); });
        tipBtn.addEventListener('focus', function () { bnpl.classList.add('is-tip'); });
        tipBtn.addEventListener('blur', function () { bnpl.classList.remove('is-tip'); });
        tipBtn.addEventListener('click', function (e) {
          e.preventDefault();
          if (canHover.matches) { bnpl.classList.remove('is-tip'); openDialog(sec, 'bnpl'); }
          else bnpl.classList.toggle('is-tip');
        });
        d.addEventListener('click', function (e) {
          if (!canHover.matches && !e.target.closest('[data-ovc-bnpl]')) bnpl.classList.remove('is-tip');
        });
      }
    }
  }

  function planVariant() {
    var c = planCfg;
    if (!c || !c.variants || !c.variants.length) return null;
    var match = String(state.sizeMatch || '').toLowerCase();
    var i, v;
    if (c.sizeOption >= 0 && match) {
      for (i = 0; i < c.variants.length; i++) {
        v = c.variants[i];
        if (v.available && String(v.options[c.sizeOption] || '').toLowerCase() === match) return v;
      }
    }
    for (i = 0; i < c.variants.length; i++) if (c.variants[i].available) return c.variants[i];
    return c.variants[0];
  }

  // Only matters if sizes are priced differently: keep the shown price in step with the size
  function updatePlanPrice(sec) {
    var el = sec.querySelector('[data-ovc-plan-price]');
    var v = planVariant();
    if (!el || !v || !state.size) return;
    var first = planCfg.variants[0];
    if (first && first.price === v.price) return;
    el.textContent = money(v.price);
  }
  function money(cents) {
    var n = cents / 100;
    var s = n.toLocaleString('en-US', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 });
    return '$' + s;
  }

  /* Popups */
  function openDialog(scope, name) {
    var dlg = (scope || d).querySelector('[data-ovc-dialog="' + name + '"]') || d.querySelector('[data-ovc-dialog="' + name + '"]');
    if (!dlg) return;
    dlg._ovcReturn = d.activeElement;
    if (typeof dlg.showModal === 'function') { if (!dlg.open) dlg.showModal(); }
    else { dlg.setAttribute('open', ''); dlg.classList.add('is-fallback'); }
  }
  function closeDialog(dlg) {
    if (!dlg) return;
    if (typeof dlg.close === 'function' && dlg.open) dlg.close();
    else { dlg.removeAttribute('open'); dlg.classList.remove('is-fallback'); }
  }
  d.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-ovc-open]');
    if (opener) { e.preventDefault(); openDialog(opener.closest('[data-ovc-step]'), opener.getAttribute('data-ovc-open')); return; }
    var closer = e.target.closest('[data-ovc-close]');
    if (closer) { e.preventDefault(); closeDialog(closer.closest('dialog')); return; }
    // Click on the dark backdrop closes the popup
    if (e.target.tagName === 'DIALOG' && e.target.hasAttribute('data-ovc-dialog')) {
      var r = e.target.getBoundingClientRect();
      var inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
      var card = e.target.firstElementChild;
      if (card) {
        var c = card.getBoundingClientRect();
        inside = e.clientX >= c.left && e.clientX <= c.right && e.clientY >= c.top && e.clientY <= c.bottom;
      }
      if (!inside) closeDialog(e.target);
    }
  });
  d.addEventListener('close', function (e) {
    var dlg = e.target;
    if (dlg && dlg._ovcReturn && typeof dlg._ovcReturn.focus === 'function') {
      try { dlg._ovcReturn.focus({ preventScroll: true }); } catch (err) {}
    }
  }, true);

  /* ==========================================================================
     4. Extras
     ========================================================================== */
  var extraCfg = {};
  function initExtras() {
    each('[data-ovc-step]', function (sec) {
      var cfg = readJSON('[data-ovc-extra-config]', sec);
      if (!cfg) return;
      extraCfg[cfg.key] = cfg;
      if (sec._ovc) return;
      sec._ovc = true;
      sec.addEventListener('click', function (e) {
        var b = e.target.closest('[data-ovc-choice]');
        if (!b) return;
        e.preventDefault();
        state.extras[cfg.key] = b.getAttribute('data-ovc-choice') === 'add';
        save();
        next();
      });
    });
  }

  /* ==========================================================================
     5. Checkout handoff
     ========================================================================== */
  var busy = false;
  function handoffEl() { return d.querySelector('[data-ovc-step="checkout"]'); }
  function hideHandoff() {
    var h = handoffEl();
    if (h) { h.hidden = true; h.classList.remove('is-error'); }
    busy = false;
  }

  function post(path, body) {
    return w.fetch(root + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body || {})
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (j) {
        if (!r.ok) {
          var err = new Error((j && (j.description || j.message)) || ('HTTP ' + r.status));
          err.status = r.status;
          throw err;
        }
        return j;
      });
    });
  }
  function wait(ms) { return new Promise(function (res) { setTimeout(res, Math.max(0, ms)); }); }

  function cartItems() {
    var items = [];
    var v = planVariant();
    if (!v) return null;
    var plan = { id: v.id, quantity: 1 };
    if (planCfg.sellingPlan) plan.selling_plan = planCfg.sellingPlan;
    if (planCfg.sizeOption < 0 && state.size) {
      plan.properties = {};
      plan.properties[planCfg.sizeProperty || 'Shirt size'] = state.size;
    }
    items.push(plan);
    steps.forEach(function (s) {
      var c = extraCfg[s.key];
      if (c && state.extras[s.key] && c.variantId && c.available !== false) items.push({ id: c.variantId, quantity: 1 });
    });
    return items;
  }

  function checkoutUrl(cfg) {
    var q = [];
    if (state.email) q.push('checkout%5Bemail%5D=' + encodeURIComponent(state.email));
    if (cfg.discount) q.push('discount=' + encodeURIComponent(cfg.discount));
    return root + 'checkout' + (q.length ? '?' + q.join('&') : '');
  }

  function startCheckout() {
    var h = handoffEl();
    if (busy) return;
    var cfg = readJSON('[data-ovc-handoff-config]') || {};
    if (h) { h.hidden = false; h.classList.remove('is-error'); }
    setProgress(4);
    track('checkout');
    if (designMode) return; // never touch the cart from the theme editor

    var items = cartItems();
    if (!items) { fail(new Error('Setup: the Waifu Overlord Annual product isn\'t picked yet. Theme editor → "Checkout: 2 Plan" → "Waifu Overlord Annual product".')); return; }
    var missing = steps.filter(function (s) {
      var c = extraCfg[s.key];
      return c && state.extras[s.key] && !c.variantId;
    }).map(function (s) { return extraCfg[s.key].title; });
    if (missing.length) { fail(new Error('Setup: no product is picked for "' + missing.join('", "') + '". Theme editor → that extra\'s section → "Product added to the order".')); return; }
    busy = true;
    var started = Date.now();
    var gifts = (cfg.gifts || []).filter(function (g) {
      return g && g.id && g.available && (g.price === 0 || cfg.giftsAlways);
    });
    var skipped = [];

    // Shopify lists the newest cart line first, and checkout keeps that order. So the free goodies go in
    // first (last one first) and the paid items after (plan last), which shows at checkout as:
    // plan, extras, then every free goodie in the order picked in the theme editor.
    (cfg.clearCart ? post('cart/clear.js') : Promise.resolve())
      .then(function () { return addFree(gifts.slice().reverse(), !cfg.clearCart, skipped); })
      .then(function () { return post('cart/add.js', { items: items.slice().reverse() }); })
      .then(function () {
        var attrs = { 'Overlord flow': 'Black November 2026' };
        if (skipped.length) attrs['Free goodies not added'] = skipped.join(', ');
        return post('cart/update.js', { attributes: attrs }).catch(function () {});
      })
      .then(function () { return wait((cfg.minMs || 0) - (Date.now() - started)); })
      .then(function () { w.location.href = checkoutUrl(cfg); })
      .catch(fail);
  }

  function getCart() {
    return w.fetch(root + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .catch(function () { return { items: [] }; });
  }
  function inCart(cart) {
    var ids = {};
    ((cart && cart.items) || []).forEach(function (it) { ids[it.variant_id || it.id] = true; });
    return ids;
  }

  // Adds the free goodies (already in add order). One call when it works; if a goodie is sold out or
  // not on the Online Store, they go in one by one and only that one is left out, so a free item never
  // blocks the order. Anything left out is written on the order as "Free goodies not added".
  function addFree(list, skipPresent, skipped) {
    if (!list.length) return Promise.resolve();
    var start = skipPresent ? getCart() : Promise.resolve(null);
    return start.then(function (cart) {
      var have = inCart(cart);
      var todo = list.filter(function (g) { return !have[g.id]; });
      if (!todo.length) return null;
      return post('cart/add.js', { items: todo.map(function (g) { return { id: g.id, quantity: 1 }; }) })
        .catch(function () {
          return getCart().then(function (c) {
            var got = inCart(c);
            return todo.reduce(function (chain, g) {
              return chain.then(function () {
                if (got[g.id]) return null;
                return post('cart/add.js', { items: [{ id: g.id, quantity: 1 }] }).catch(function (err) {
                  skipped.push(g.title || String(g.id));
                  if (w.console) console.warn('[Overlord checkout] Free goodie not added:', g.title, err.message);
                });
              });
            }, Promise.resolve());
          });
        });
    });
  }

  function fail(err) {
    busy = false;
    var h = handoffEl();
    if (w.console) console.error('[Overlord checkout]', err && err.message);
    if (!h) { w.alert(err && err.message ? err.message : 'Something went wrong.'); return; }
    h.hidden = false;
    h.classList.add('is-error');
    var box = h.querySelector('[data-ovc-error]');
    if (box) box.hidden = false;
    var detail = h.querySelector('[data-ovc-error-detail]');
    if (detail) detail.textContent = err && err.message ? err.message : '';
    var retry = h.querySelector('[data-ovc-retry]');
    if (retry) try { retry.focus(); } catch (e) {}
  }

  d.addEventListener('click', function (e) {
    if (e.target.closest('[data-ovc-retry]')) {
      e.preventDefault();
      var h = handoffEl();
      var box = h && h.querySelector('[data-ovc-error]');
      if (box) box.hidden = true;
      if (h) h.classList.remove('is-error');
      startCheckout();
    }
  });
  // The "go back" button inside the error box closes the overlay
  d.addEventListener('click', function (e) {
    var b = e.target.closest('[data-ovc-back]');
    if (b && b.closest('[data-ovc-step="checkout"]')) {
      var h = handoffEl();
      var box = h && h.querySelector('[data-ovc-error]');
      if (box) box.hidden = true;
      hideHandoff();
      setProgress(steps[current] ? steps[current].progress : 3);
    }
  }, true);

  /* ==========================================================================
     6. Media
     ========================================================================== */
  function loadVideo(v) {
    if (v.getAttribute('data-loaded')) return;
    v.setAttribute('data-loaded', '1');
    Array.prototype.forEach.call(v.querySelectorAll('source[data-src]'), function (s) {
      s.src = s.getAttribute('data-src');
      s.removeAttribute('data-src');
    });
    if (reduceMotion.matches) {
      v.addEventListener('loadedmetadata', function () { try { v.currentTime = 0.01; } catch (e) {} }, { once: true });
    }
    v.load();
  }
  function playVideo(v) {
    if (reduceMotion.matches) return;
    var p = v.play();
    if (p && typeof p.catch === 'function') p.catch(function () {});
  }
  var videoIO = hasIO ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      var v = e.target;
      if (e.isIntersecting) { loadVideo(v); playVideo(v); }
      else if (!v.paused) v.pause();
    });
  }, { rootMargin: '300px 0px' }) : null;

  function initVideos(scope) {
    each('video[data-ovl-video]', function (v) {
      if (v._ovc) return;
      v._ovc = true;
      if (v.hasAttribute('data-eager')) {
        v.setAttribute('data-loaded', '1');
        if (reduceMotion.matches) v.pause();
      }
      if (videoIO) videoIO.observe(v);
      else { loadVideo(v); playVideo(v); }
    }, scope);
  }

  // Warm up the next step's top videos while the shopper reads this one
  var prefetchTimer = null;
  function prefetchNext(i) {
    clearTimeout(prefetchTimer);
    prefetchTimer = setTimeout(function () {
      var n = steps[i + 1];
      if (!n || n.key === 'checkout') return;
      each('.ovc-x__heromedia video[data-ovl-video], .ovc-x__char video[data-ovl-video]', function (v) {
        v.preload = 'auto';
        loadVideo(v);
      }, n.el);
    }, 1500);
  }

  /* Swipe galleries */
  function initGalleries(scope) {
    each('[data-ovc-gallery]', function (g) {
      if (g._ovc) return;
      g._ovc = true;
      var track = g.querySelector('[data-ovc-gal-track]');
      var dots = g.querySelectorAll('[data-ovc-dot]');
      if (!track) return;
      function step() {
        var first = track.firstElementChild;
        if (!first) return track.clientWidth;
        var gap = parseFloat(w.getComputedStyle(track).columnGap) || 0;
        return first.getBoundingClientRect().width + gap;
      }
      function active() { return Math.round(track.scrollLeft / Math.max(1, step())); }
      var t;
      track.addEventListener('scroll', function () {
        clearTimeout(t);
        t = setTimeout(function () {
          var a = active();
          Array.prototype.forEach.call(dots, function (dot, i) {
            if (i === a) dot.setAttribute('aria-current', 'true'); else dot.removeAttribute('aria-current');
          });
        }, 60);
      }, { passive: true });
      Array.prototype.forEach.call(dots, function (dot) {
        dot.addEventListener('click', function () {
          var i = parseInt(dot.getAttribute('data-ovc-dot'), 10) || 0;
          track.scrollTo({ left: i * step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        });
      });
      // Click a slide on desktop to go to the next one (as in the prototype)
      track.addEventListener('click', function (e) {
        if (track._ovcDragged || !canHover.matches) return;
        var count = track.children.length;
        var a = active();
        track.scrollTo({ left: ((a + 1) % count) * step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        e.preventDefault();
      });
    }, scope);
  }

  /* Drag to scroll with a mouse (touch scrolls natively) */
  function initDrag(scope) {
    each('[data-ovc-drag]', function (rail) {
      if (rail._ovcDrag) return;
      rail._ovcDrag = true;
      var down = false, startX = 0, startLeft = 0;
      rail.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        down = true; rail._ovcDragged = false; startX = e.clientX; startLeft = rail.scrollLeft;
      });
      rail.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        if (!rail._ovcDragged && Math.abs(dx) > 5) {
          rail._ovcDragged = true;
          rail.classList.add('is-dragging');
          try { rail.setPointerCapture(e.pointerId); } catch (err) {}
        }
        if (rail._ovcDragged) { rail.scrollLeft = startLeft - dx; e.preventDefault(); }
      });
      function end() {
        if (!down) return;
        down = false;
        if (rail._ovcDragged) {
          rail.classList.remove('is-dragging');
          // Snap to the nearest slide after a drag
          var first = rail.firstElementChild;
          if (first && rail.hasAttribute('data-ovc-gal-track')) {
            var gap = parseFloat(w.getComputedStyle(rail).columnGap) || 0;
            var s = first.getBoundingClientRect().width + gap;
            rail.scrollTo({ left: Math.round(rail.scrollLeft / s) * s, behavior: 'smooth' });
          }
          setTimeout(function () { rail._ovcDragged = false; }, 0);
        }
      }
      rail.addEventListener('pointerup', end);
      rail.addEventListener('pointercancel', end);
      rail.addEventListener('lostpointercapture', end);
      rail.addEventListener('click', function (e) { if (rail._ovcDragged) { e.preventDefault(); e.stopPropagation(); } }, true);
      rail.addEventListener('dragstart', function (e) { e.preventDefault(); });
    }, scope);
  }

  /* One-line titles: shrink a little if edited copy runs long */
  function fitText() {
    each('[data-ovc-fit]', function (el) {
      el.style.fontSize = '';
      if (w.getComputedStyle(el).whiteSpace !== 'nowrap') return;
      var avail = el.clientWidth, need = el.scrollWidth;
      if (avail && need > avail + 1) {
        var fs = parseFloat(w.getComputedStyle(el).fontSize);
        el.style.fontSize = Math.max(12, Math.floor(fs * (avail / need) * 0.99 * 100) / 100) + 'px';
      }
    });
  }

  /* ==========================================================================
     Start
     ========================================================================== */
  function initAll(scope) {
    collectSteps();
    initEmail();
    initPlan();
    initExtras();
    initVideos(scope);
    initGalleries(scope);
    initDrag(scope);
  }

  function start() {
    initAll();
    if (!steps.length) return;
    var key = w.location.hash.slice(1);
    var i = indexOf(key);
    if (i < 0 || steps[i].key === 'checkout') i = 0;
    if (!allowed(i)) i = firstAllowedBefore(i);
    try { w.history.replaceState({ ovc: steps[i].key, ovcDepth: 0 }, '', '#' + steps[i].key); } catch (e) {}
    show(i, true);
    if (d.fonts && d.fonts.ready) d.fonts.ready.then(fitText);
    var t;
    w.addEventListener('resize', function () { clearTimeout(t); t = setTimeout(fitText, 120); }, { passive: true });
  }

  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', start);
  else start();

  /* Theme editor: show the step whose section is selected */
  if (designMode) {
    d.addEventListener('shopify:section:load', function (e) {
      var prev = current >= 0 && steps[current] ? steps[current].key : null;
      initAll(e.target);
      var sec = e.target.querySelector('[data-ovc-step]');
      var i = sec ? indexOf(sec.getAttribute('data-ovc-step')) : indexOf(prev);
      if (i >= 0) show(i, true);
    });
    d.addEventListener('shopify:section:select', function (e) {
      var sec = e.target.querySelector('[data-ovc-step]');
      if (!sec) return;
      var i = indexOf(sec.getAttribute('data-ovc-step'));
      if (i >= 0) { hideHandoff(); show(i, true); if (sec.getAttribute('data-ovc-step') === 'checkout') sec.hidden = false; }
    });
  }
})();
